import { useState } from 'react';
import type { VisitTemplate } from '../data/templates';

interface Props {
  template: VisitTemplate;
}

const FILTER_OPTIONS = ['All types', 'Lab results', 'Imaging', 'Procedure', 'Referral', 'Documents signed'] as const;

export function TimelineTab({ template }: Props) {
  const [filter, setFilter] = useState<string>('All types');
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);

  const docs = template.documents ?? [];
  const filtered = filter === 'All types'
    ? docs
    : docs.filter((d) => d.type === filter);

  const viewingDetails = viewingDoc
    ? docs.find((d) => d.file === viewingDoc)
    : null;

  if (docs.length === 0) {
    return (
      <div className="tab-panel">
        <div className="timeline-header">
          <h3 className="timeline-title">Documents &amp; Results</h3>
        </div>
        <p style={{ color: 'var(--pf-gray-500)', textAlign: 'center', padding: '24px 16px' }}>
          No documents available for this visit.
        </p>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div className="timeline-header">
        <h3 className="timeline-title">Documents &amp; Results</h3>
        <select
          className="timeline-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <table className="med-table timeline-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Details</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((doc, i) => (
            <tr
              key={i}
              className={doc.file ? 'timeline-row-clickable' : undefined}
              onClick={doc.file ? () => setViewingDoc(doc.file!) : undefined}
            >
              <td className="timeline-date">{doc.date}</td>
              <td>
                <span className={`timeline-type-badge timeline-type-${doc.type.toLowerCase().replace(/\s+/g, '-')}`}>
                  {doc.type}
                </span>
              </td>
              <td>{doc.details}</td>
              <td>{doc.file && <span className="timeline-view-pdf">View PDF</span>}</td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={4} className="timeline-empty">No documents match this filter</td>
            </tr>
          )}
        </tbody>
      </table>

      {viewingDoc && viewingDetails && (
        <div className="document-viewer">
          <div className="document-viewer-header">
            <span>{viewingDetails.type}: {viewingDetails.details} ({viewingDetails.date})</span>
            <button className="document-viewer-close" onClick={() => setViewingDoc(null)}>&times;</button>
          </div>
          <iframe src={viewingDoc} title="Document viewer" />
        </div>
      )}
    </div>
  );
}
